import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import ProblemCode from "../types/problemCode";
import SearchResults from "../types/searchResults";
import SearchParams, { defaultParams } from "../types/searchParams";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface ProblemCodeState {
  problemCode: ProblemCode;
  problemCodeSearchResults?: SearchResults;
  problemCodeFilter: SearchParams;
  status: "idle" | "loading" | "failed";
}

const initialState: ProblemCodeState = {
  problemCode: {
    id: 0,
    name: "",
    category: undefined,
  },
  problemCodeFilter: defaultParams,
  problemCodeSearchResults: undefined,
  status: "idle",
};

export const getProblemCodeId = createAsyncThunk<ProblemCode, number>(
  "problem-code/get/id",
  async (id: number) => {
    const response = await http.get<ProblemCode>(`/problemcode/${id}`);
    return response.data;
  }
);

export const editProblemCode = createAsyncThunk<ProblemCode, ProblemCode>(
  "problem-code/edit",
  async (problemCode: ProblemCode) => {
    const response = await http.put<ProblemCode>(
      `/problemcode/${problemCode.id}`,
      problemCode,
      {
        params: {
          successToast: true,
        },
      }
    );
    return response.data;
  }
);

export const deleteProblemCode = createAsyncThunk<ProblemCode, number>(
  "problem-code/delete",
  async (id: number) => {
    const response = await http.delete<ProblemCode>(`/problemcode/${id}`);
    return response.data;
  }
);

export const postProblemCode = createAsyncThunk<ProblemCode, ProblemCode>(
  "problem-code/post",
  async (problemCode: ProblemCode) => {
    const response = await http.post<ProblemCode>(`/problemcode`, problemCode, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  }
);

export const searchProblemCodes = createAsyncThunk<SearchResults, SearchParams>(
  "problem-code/search",
  async (search: SearchParams) => {
    let qs = getSearchQueryString(search);
    const response = await http.get<SearchResults>(`problemcode/search?${qs}`);
    return response.data;
  }
);

const problemCodeSlice = createSlice({
  name: "problemCode",
  initialState,
  reducers: {
    resetProblemCode(state) {
      state.problemCode = { ...initialState.problemCode };
    },
    setProblemCodeFilter(state, action) {
      state.problemCodeFilter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProblemCodeId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getProblemCodeId.fulfilled, (state, action) => {
        state.status = "idle";
        state.problemCode = action.payload;
      })
      .addCase(getProblemCodeId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editProblemCode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editProblemCode.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(editProblemCode.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteProblemCode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteProblemCode.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(deleteProblemCode.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postProblemCode.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postProblemCode.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(postProblemCode.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchProblemCodes.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchProblemCodes.fulfilled, (state, action) => {
        state.status = "idle";
        state.problemCodeSearchResults = action.payload;
      })
      .addCase(searchProblemCodes.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default problemCodeSlice.reducer;
export const { resetProblemCode, setProblemCodeFilter } =
  problemCodeSlice.actions;
